import { z } from 'zod'
import { CASE_CLIENT_ROLES, CASE_TYPES, type CaseType } from '../enums'
import { caseClientInputSchema } from './caseClient'
import {
  csvEnum,
  optionalDate,
  optionalId,
  optionalMoney,
  optionalText,
  requiredMoney,
  requiredText,
} from './common'

const caseFields = z.object({
  projectId: optionalId(),
  name: requiredText(200, 'שם התיק הוא שדה חובה'),
  description: optionalText(2000),
  caseType: z.enum(CASE_TYPES),
  clientRole: z.enum(CASE_CLIENT_ROLES),
  city: optionalText(100),
  street: optionalText(150),
  buildingNo: optionalText(20),
  aptNo: optionalText(20),
  transactionDate: optionalDate(),
  feeAmount: optionalMoney(),
})

type AddressBearing = {
  caseType: CaseType
  projectId?: string
  city?: string
  street?: string
  buildingNo?: string
  aptNo?: string
}

/**
 * A will has no property, so its address fields are cleared rather than rejected —
 * switching the type on an existing case must not fail on stale form state. A case
 * inside a project takes its city and street from the project (one source of truth);
 * only the unit within the property stays on the case.
 */
const clearInheritedAddress = <T extends AddressBearing>(v: T): T => {
  if (v.caseType === 'WILL') {
    return { ...v, city: undefined, street: undefined, buildingNo: undefined, aptNo: undefined }
  }
  return v.projectId ? { ...v, city: undefined, street: undefined } : v
}

export const caseInputSchema = caseFields.transform(clearInheritedAddress)

export type CaseInput = z.infer<typeof caseInputSchema>

/**
 * Creation also takes the clients joining the case, linked in the same transaction, so
 * the case never exists for a moment without them. Each entry obeys the same ownership
 * rule as `POST /cases/:id/clients`.
 */
export const caseCreateSchema = caseFields
  .extend({ clients: z.array(caseClientInputSchema).max(50, 'עד 50 לקוחות בתיק').default([]) })
  .transform(clearInheritedAddress)

export type CaseCreateInput = z.infer<typeof caseCreateSchema>

export const caseQuerySchema = z.object({
  search: optionalText(200),
  projectId: optionalId(),
  clientId: optionalId(),
  caseType: csvEnum(CASE_TYPES),
  clientRole: csvEnum(CASE_CLIENT_ROLES),
})

export type CaseQuery = z.infer<typeof caseQuerySchema>

export type CaseDto = {
  id: string
  projectId: string | null
  name: string
  description: string | null
  caseType: (typeof CASE_TYPES)[number]
  clientRole: (typeof CASE_CLIENT_ROLES)[number]
  city: string | null
  street: string | null
  buildingNo: string | null
  aptNo: string | null
  transactionDate: string | null
  feeAmount: number | null
  driveFolderId: string | null
  createdAt: string
  updatedAt: string
}

// ── Payments to the office (fee vs. what was actually received) ───────────────

export const casePaymentInputSchema = z.object({
  amount: requiredMoney(),
  /** Defaults to today on the server. */
  paidAt: optionalDate(),
  method: optionalText(60),
  note: optionalText(500),
})

export type CasePaymentInput = z.infer<typeof casePaymentInputSchema>

export type CasePaymentDto = {
  id: string
  caseId: string
  amount: number
  paidAt: string
  method: string | null
  note: string | null
  createdById: string | null
  createdByName: string | null
  createdAt: string
}

export type CasePaymentsSummary = {
  /** The agreed fee (`null` when not set yet). */
  feeAmount: number | null
  totalPaid: number
  /** `feeAmount − totalPaid`; `null` while there is no fee to measure against. */
  balance: number | null
  payments: CasePaymentDto[]
}
